import { Box, Flex, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'

import Gift from '@/assets/icons/gift.svg'
import { EEA_COUNTRIES } from '@/i18n/country'

import styles from './index.module.css'

const REGIONAL_SITE_LOCALES = [
  'us',
  'es-us',
  'ca',
  'fr-ca',
  'sg',
  'au',
  'br',
  'en-br',
  'pt-br',
  'ae',
  'en-ae',
]

const REGIONAL_SITE_COUNTRIES = ['US', 'CA', 'SG', 'AU', 'BR', 'AE']

const GLOBAL_SITE_LOCALES = ['en', 'es', 'fr', 'zh', 'id', 'ru', 'vi']
const GLOBAL_SITE_ELIGIBLE_COUNTRIES = ['HK', 'IS', 'NO', 'LI']

/**
 * Determine if the welcome bonus tips should be shown based on locale and country code
 * @param locale - The user's locale
 * @param countryCode - The user's country code
 * @returns true if the welcome bonus tips should be shown
 */
const shouldShowWelcomeBonusTips = (locale: string, countryCode: string): boolean => {
  const normalizedLocale = locale.toLowerCase()
  const normalizedCountryCode = countryCode.toUpperCase()

  // Regional site ["us", "ca", "sg", "au", "br", "ae"]
  if (REGIONAL_SITE_COUNTRIES.includes(normalizedCountryCode)) {
    return REGIONAL_SITE_LOCALES.includes(normalizedLocale)
  }

  // In global site and IP is in "HK", "IS", "NO", "LI"
  if (GLOBAL_SITE_LOCALES.includes(normalizedLocale)) {
    return GLOBAL_SITE_ELIGIBLE_COUNTRIES.includes(normalizedCountryCode)
  }

  // In EEA site and IP is in EEA countries
  if (normalizedLocale === 'eea') {
    return EEA_COUNTRIES.includes(normalizedCountryCode)
  }

  return false
}

interface WelcomeBonusTipsProps {
  locale: string
  countryCode: string
}

export default function WelcomeBonusTips({ locale, countryCode }: WelcomeBonusTipsProps) {
  const t = useTranslations('tradeWidget')

  if (!shouldShowWelcomeBonusTips(locale, countryCode)) {
    return null
  }

  return (
    <Flex className={styles.welcomeBonusTips}>
      <Gift height={20} fill={'var(--common-white)'} />
      <Box flex={1} ml={8}>
        <Text variant={'subtitle'} fz={12} c={'var(--content-secondary)'}>
          {t('welcomeBonusTips')}
        </Text>
      </Box>
    </Flex>
  )
}
